import { useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { AlertTriangle, CheckCircle2, KeyRound, Plug, Plus } from 'lucide-react';
import type { ModelConfigView } from '@autopilot/contracts';
import { api, type ConnectionTestResult, type ModelUpsert } from '../../api/client';
import { qk } from '../../api/hooks';
import { ApiError } from '../../api/errors';
import { EmptyState, ErrorAlert, QueryBoundary } from '../../components/States';
import { ToneBadge } from '../../components/StatusBadge';
import { PROVIDER_LABEL } from '../../lib/labels';
import { formatDateTime } from '../../lib/format';
import { secretRequired } from '../../lib/models';

const ROLE_LABEL = { GENERATOR: 'Generator', CORRECTNESS: 'Richtigkeitsprüfer', ERROR_RISK: 'Fehlerprüfer' } as const;

export function ModelsTab() {
  const [edit, setEdit] = useState<ModelConfigView | 'new' | null>(null);
  const [secretFor, setSecretFor] = useState<ModelConfigView | null>(null);
  const [tests, setTests] = useState<Record<string, ConnectionTestResult | 'pending' | { error: unknown }>>({});
  const q = useQuery({ queryKey: qk.models, queryFn: api.listModels });
  const runTest = async (m: ModelConfigView) => {
    setTests((t) => ({ ...t, [m.id]: 'pending' }));
    try {
      const r = await api.testModel(m.id);
      setTests((t) => ({ ...t, [m.id]: r }));
    } catch (error) {
      setTests((t) => ({ ...t, [m.id]: { error } }));
    }
  };
  const items = q.data ?? [];
  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 820, flex: 1 }}>
          Modelle werden am Batch bzw. an der Gesellschaftsregel mit Version festgehalten. Zugangsschlüssel werden im Key Vault gespeichert und können nur ersetzt, nie angezeigt werden. Die Verbindungsprüfung verwendet keine Kundendaten.
        </Typography>
        <Button variant="contained" startIcon={<Plus size={18} />} onClick={() => setEdit('new')}>
          Modell anlegen
        </Button>
      </Box>
      <Card>
        <QueryBoundary query={q} action="Modelle laden" loadingRows={4}>
          {items.length === 0 ? (
            <EmptyState title="Noch keine Modelle" description="Legen Sie mindestens ein Generator- und ein Prüfmodell an." />
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table aria-label="Modelle und Zugangsdaten" sx={{ minWidth: 960 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Anbieter / Modell-ID</TableCell>
                    <TableCell>Rollen</TableCell>
                    <TableCell>Grenzen</TableCell>
                    <TableCell>Zugangsschlüssel</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((m) => {
                    const t = tests[m.id];
                    return (
                      <TableRow key={m.id} data-testid={`model-row-${m.name}`}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {m.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Version {m.version}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {PROVIDER_LABEL[m.provider]}
                          <Typography variant="caption" component="div" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                            {m.modelId}
                          </Typography>
                        </TableCell>
                        <TableCell>{m.roles.map((r) => ROLE_LABEL[r]).join(', ')}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="caption" component="div">Input-Budget {m.inputBudgetTokens.toLocaleString('de-DE')}</Typography>
                          <Typography variant="caption" component="div">Output {m.outputLimitTokens.toLocaleString('de-DE')} · {m.timeoutSeconds}s</Typography>
                        </TableCell>
                        <TableCell>
                          {m.secret.configured ? (
                            <>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }} data-testid="secret-masked">
                                {m.secret.masked ?? '••••••••'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                ersetzt {formatDateTime(m.secret.updatedAt)}
                              </Typography>
                            </>
                          ) : secretRequired(m) ? (
                            <ToneBadge tone="warning" label="Nicht hinterlegt" />
                          ) : (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              Nicht erforderlich (Mock)
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {m.active ? <ToneBadge tone="success" label="Aktiv" /> : <ToneBadge tone="neutral" label="Deaktiviert" />}
                          {t && t !== 'pending' && (
                            <Typography
                              variant="caption"
                              component="div"
                              role="status"
                              sx={{ mt: 0.5, display: 'flex', gap: 0.5, color: 'error' in t ? 'error.dark' : t.ok ? 'success.dark' : 'error.dark' }}
                            >
                              {'error' in t ? (
                                <>
                                  <AlertTriangle size={13} /> Test fehlgeschlagen
                                </>
                              ) : t.ok ? (
                                <>
                                  <CheckCircle2 size={13} /> {t.message}
                                </>
                              ) : (
                                <>
                                  <AlertTriangle size={13} /> {t.message}
                                </>
                              )}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25, '& button': { whiteSpace: 'nowrap' } }}>
                          <Button size="small" onClick={() => setEdit(m)}>
                            Bearbeiten
                          </Button>
                          <Button size="small" startIcon={<KeyRound size={14} />} onClick={() => setSecretFor(m)}>
                            {m.secret.configured ? 'Schlüssel ersetzen' : 'Schlüssel setzen'}
                          </Button>
                          <Button size="small" startIcon={<Plug size={14} />} onClick={() => void runTest(m)} disabled={t === 'pending'}>
                            {t === 'pending' ? 'Teste …' : 'Verbindung testen'}
                          </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </QueryBoundary>
      </Card>
      {edit && <ModelDialog model={edit === 'new' ? null : edit} onClose={() => setEdit(null)} />}
      {secretFor && <SecretDialog model={secretFor} onClose={() => setSecretFor(null)} />}
    </>
  );
}

function ModelDialog({ model, onClose }: { model: ModelConfigView | null; onClose: () => void }) {
  const id = useId();
  const qc = useQueryClient();
  const [f, setF] = useState<ModelUpsert>(
    model
      ? { name: model.name, provider: model.provider, modelId: model.modelId, endpoint: model.endpoint, roles: model.roles, contextLimitTokens: model.contextLimitTokens, inputBudgetTokens: model.inputBudgetTokens, outputLimitTokens: model.outputLimitTokens, timeoutSeconds: model.timeoutSeconds, active: model.active, expectedRowVersion: model.rowVersion }
      : { name: '', provider: 'OPENAI', modelId: '', endpoint: null, roles: ['GENERATOR'], contextLimitTokens: 128000, inputBudgetTokens: 24000, outputLimitTokens: 4000, timeoutSeconds: 120, active: true },
  );
  const save = useMutation({
    mutationFn: () => (model ? api.updateModel(model.id, f) : api.createModel(f)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.models });
      onClose();
    },
  });
  const fe = save.error instanceof ApiError ? save.error.fieldErrors : {};
  const num = (k: keyof ModelUpsert) => ({
    value: String(f[k] ?? ''),
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: Number(e.target.value) }),
    type: 'number',
    error: !!fe[k],
    helperText: fe[k],
  });
  return (
    <Dialog open onClose={onClose} aria-labelledby={id} maxWidth="sm" fullWidth>
      <DialogTitle id={id}>{model ? 'Modell bearbeiten' : 'Modell anlegen'}</DialogTitle>
      <DialogContent dividers sx={{ display: 'grid', gap: 2.5 }}>
        {model && <Alert severity="info">Änderungen erzeugen eine neue Modellversion. Laufende Batches und aktive Gesellschaftsregeln behalten ihre festgehaltene Version.</Alert>}
        <TextField label="Anzeigename" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} error={!!fe.name} helperText={fe.name} autoFocus />
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr' }}>
          <TextField select label="Anbieter" value={f.provider} onChange={(e) => setF({ ...f, provider: e.target.value as ModelUpsert['provider'] })}>
            {Object.entries(PROVIDER_LABEL).map(([k, v]) => (
              <MenuItem key={k} value={k}>
                {v}
              </MenuItem>
            ))}
          </TextField>
          <TextField label="Modell-ID" value={f.modelId} onChange={(e) => setF({ ...f, modelId: e.target.value })} error={!!fe.modelId} helperText={fe.modelId ?? 'Konkrete ID beim Anbieter'} />
        </Box>
        <TextField label="Endpoint (optional)" value={f.endpoint ?? ''} onChange={(e) => setF({ ...f, endpoint: e.target.value || null })} error={!!fe.endpoint} helperText={fe.endpoint ?? 'Nur erlaubte Anbieter-Endpunkte (Allowlist).'} />
        <Box>
          <FormLabel component="legend" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
            Rollen
          </FormLabel>
          <FormGroup row>
            {(Object.keys(ROLE_LABEL) as (keyof typeof ROLE_LABEL)[]).map((r) => (
              <FormControlLabel
                key={r}
                control={<Checkbox size="small" checked={f.roles.includes(r)} onChange={(e) => setF({ ...f, roles: e.target.checked ? [...f.roles, r] : f.roles.filter((x) => x !== r) })} />}
                label={ROLE_LABEL[r]}
              />
            ))}
          </FormGroup>
        </Box>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr' }}>
          <TextField label="Kontextlimit (Tokens)" {...num('contextLimitTokens')} />
          <TextField label="Input-Budget (Tokens)" {...num('inputBudgetTokens')} />
          <TextField label="Output-Limit (Tokens)" {...num('outputLimitTokens')} />
          <TextField label="Timeout (Sekunden)" {...num('timeoutSeconds')} />
        </Box>
        <FormControlLabel control={<Switch checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />} label="Aktiv" />
        {save.error && !Object.keys(fe).length ? <ErrorAlert error={save.error} action="Modell speichern" /> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button variant="contained" onClick={() => save.mutate()} disabled={!f.name.trim() || !f.modelId.trim() || f.roles.length === 0 || save.isPending}>
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SecretDialog({ model, onClose }: { model: ModelConfigView; onClose: () => void }) {
  const id = useId();
  const qc = useQueryClient();
  const [secret, setSecret] = useState('');
  const save = useMutation({
    mutationFn: () => api.setModelSecret(model.id, secret),
    onSuccess: () => {
      setSecret('');
      void qc.invalidateQueries({ queryKey: qk.models });
      onClose();
    },
  });
  const fe = save.error instanceof ApiError ? save.error.fieldErrors : {};
  return (
    <Dialog open onClose={onClose} aria-labelledby={id} maxWidth="sm" fullWidth>
      <DialogTitle id={id}>Zugangsschlüssel für „{model.name}“ {model.secret.configured ? 'ersetzen' : 'setzen'}</DialogTitle>
      <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Der Schlüssel wird nur geschrieben und im Key Vault gespeichert. Er wird danach nie wieder angezeigt – auch nicht für Admins.
          {model.secret.configured && ` Aktuell hinterlegt: ${model.secret.masked ?? '••••••••'}.`}
        </Typography>
        <TextField
          label="Neuer Schlüssel"
          type="password"
          autoComplete="new-password"
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          error={!!fe.secret}
          helperText={fe.secret ?? 'Mindestens 8 Zeichen.'}
          autoFocus
          slotProps={{ htmlInput: { 'data-testid': 'secret-input', spellCheck: false } }}
        />
        {save.error && !Object.keys(fe).length ? <ErrorAlert error={save.error} action="Schlüssel speichern" /> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button variant="contained" onClick={() => save.mutate()} disabled={secret.length < 8 || save.isPending}>
          Schlüssel speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
}
